/**
 * <carton-remove>
 *
 * Removes a built carton from the cart as a unit: the $0 anchor line AND every pack line
 * carrying the same `Carton` property, in a single /cart/update.js call.
 *
 * This is not a convenience. In the anchor model the shipping app counts anchor lines, so a
 * cart where the anchor has been removed but the packs remain ships those packs free, and a
 * cart where packs were removed but the anchor kept overcharges. The cart snippets hide the
 * per-line quantity stepper and remove button on `Carton` lines and render this instead, so
 * editing a carton is all-or-nothing.
 */
class CartRemoveCarton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', (event) => {
      event.preventDefault();
      this.removeCarton();
    });
  }

  get cartItems() {
    return (
      this.closest('cart-items') ||
      this.closest('cart-drawer-items') ||
      document.querySelector('cart-drawer-items') ||
      document.querySelector('cart-items')
    );
  }

  /** Every line key in the cart sharing this carton's label — anchor and packs alike. */
  lineKeys() {
    const label = this.dataset.cartonLabel;
    if (!label) return [];

    const scope = this.cartItems || document;
    return Array.from(scope.querySelectorAll('[data-carton-line]'))
      .filter((row) => row.dataset.cartonLabel === label)
      .map((row) => row.dataset.cartonLine)
      .filter(Boolean);
  }

  async removeCarton() {
    const keys = this.lineKeys();
    if (keys.length === 0 || this.loading) return;

    const cartItems = this.cartItems;
    this.setLoading(true);

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    const sectionsToRender = cartItems?.getSectionsToRender?.() || [];

    try {
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          updates,
          sections: sectionsToRender.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const parsedState = await response.json();

      if (parsedState.errors) {
        this.showError(parsedState.errors);
        return;
      }

      this.renderSections(parsedState, cartItems, sectionsToRender);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'carton-remove', cartData: parsedState });
      }
    } catch (error) {
      console.error('[carton-remove] Could not remove carton.', error);
      this.showError(window.cartStrings?.error);
    } finally {
      this.setLoading(false);
    }
  }

  /** Mirrors CartItems.updateQuantity's section swap so the drawer and cart page behave identically. */
  renderSections(parsedState, cartItems, sectionsToRender) {
    const isEmpty = parsedState.item_count === 0;

    cartItems?.classList.toggle('is-empty', isEmpty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);

    sectionsToRender.forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;

      const elementToReplace = container.querySelector(section.selector) || container;
      const html = parsedState.sections?.[section.section];
      if (typeof html !== 'string') return;

      elementToReplace.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? '';
  }

  setLoading(loading) {
    this.loading = loading;
    if (this.button) this.button.setAttribute('aria-busy', String(loading));
    this.classList.toggle('carton-remove--loading', loading);
  }

  showError(message) {
    const errors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (errors) errors.textContent = message || window.cartStrings?.error || '';
  }
}

/**
 * "Build Your Own Carton" placeholder product card.
 *
 * The product exists so the builder appears in collections and search; its product page uses the
 * hidden-redirect template. The collection grids run quick_add: "standard", so Refresh puts a
 * quick-add control on the card — and because the product has more than one variant that control
 * is a "Choose options" button opening a quick-add modal. The modal fetches the product page,
 * finds no product form (it is a redirect), and silently does nothing.
 *
 * Send the click to the product instead, which redirects on to the builder. The destination is
 * read from the button's own data-product-url, and the redirect target stays configurable in the
 * theme editor via the carton-redirect section — so no URL is hardcoded here.
 *
 * Listening in the capture phase so this runs before <modal-opener>'s own handler. The id suffix
 * match covers both the single-variant (product form) and multi-variant (modal) branches of
 * card-product.liquid.
 */
const CARTON_PLACEHOLDER_PRODUCT_ID = '15378013257773';

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest(
      `[id$="${CARTON_PLACEHOLDER_PRODUCT_ID}-submit"]`
    );
    if (!button) return;

    const url = button.dataset.productUrl || button.closest('.card-wrapper')?.querySelector('a[href*="/products/"]')?.href;
    if (!url) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = url;
  },
  true
);

if (!customElements.get('carton-remove')) {
  customElements.define('carton-remove', CartRemoveCarton);
}
